import type { ServerSetting } from '../../../services/adminServerSettings';
import type { GroupLimit, UnitManagerStatus } from '../../../services/adminUnits';
import SettingRow, { SettingChoice } from '../SettingRow';
import { UnitsPerGroupKey, UnitsPolicyGroups, appliedValue } from './unitsPolicy';

const GroupLimitLabels: Record<GroupLimit, string> = { Disk: 'disk', Cpu: 'CPU', Memory: 'memory' };

interface UnitsPerGroupControl {
  choices: SettingChoice[];
  note: string;
}

function unitsPerGroupControl(applied: string | null, manager: UnitManagerStatus | null): UnitsPerGroupControl {
  const cap = manager?.unitsPerGroupCap ?? null;
  const limit = manager?.unitsPerGroupBoundBy ? GroupLimitLabels[manager.unitsPerGroupBoundBy] : null;
  const stored = Number(applied) || 1;
  const most = Math.max(1, cap ?? stored);
  const choices = Array.from({ length: most }, (_, index) => String(index + 1)).map((value) => ({ value, label: value }));
  if (cap == null || limit == null) {
    return { choices, note: 'How many fit a group: not reported yet.' };
  }
  if (cap === 0) {
    return { choices, note: `None of this size fits a group (bound by ${limit}).` };
  }
  const clamped = stored > cap ? ` New groups get ${cap} while ${stored} is set.` : '';
  return { choices, note: `Up to ${cap} fit a group (bound by ${limit}).${clamped}` };
}

export default function UnitsPolicyForm({
  settings,
  manager,
  onSaved,
  onDraft,
}: {
  settings: ServerSetting[];
  /** The unit manager's status, which says how many units fit a group; null while unknown. */
  manager: UnitManagerStatus | null;
  onSaved: (saved: ServerSetting) => void;
  /** A setting's value in its control, saved or not. */
  onDraft: (key: string, draft: string) => void;
}) {
  const byKey = new Map(settings.map((setting) => [setting.key, setting]));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      {UnitsPolicyGroups.map((group) => (
        <div key={group.title}>
          <h3 className="eyebrow" style={{ margin: '0 0 10px' }}>{group.title}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 400px), 1fr))', gap: 10 }}>
            {group.settings.map(({ key, label, description, onOff }) => {
              const setting = byKey.get(key);
              if (!setting) return null;
              const control = key === UnitsPerGroupKey ? unitsPerGroupControl(appliedValue(settings, key), manager) : undefined;
              return (
                <SettingRow
                  key={key}
                  setting={setting}
                  label={label}
                  description={description}
                  onOff={onOff}
                  choices={control?.choices}
                  note={control?.note}
                  onSaved={onSaved}
                  onDraft={(draft) => onDraft(key, draft)}
                />
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
